import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoutButton from "./components/LogoutButton.jsx";

const normalizeName = (v) =>
  String(v || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export default function OngletPlanningPRAccesMedecins() {
  const navigate = useNavigate();
  const [medecins, setMedecins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch("/api/v2/medecins-gardes-pr1", { credentials: "include" });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} ${txt}`);
        }
        const json = await res.json();
        const arr = Array.isArray(json) ? json : [];
        setMedecins(arr);
      } catch (e) {
        console.error("Erreur lecture medecins PR1:", e);
        setError(e.message || "Erreur inconnue");
        setMedecins([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sorted = useMemo(() => {
    const arr = [...medecins];
    arr.sort((a, b) => {
      const na = `${a.nom || ""} ${a.prenom || ""}`.trim();
      const nb = `${b.nom || ""} ${b.prenom || ""}`.trim();
      return na.localeCompare(nb, "fr", { sensitivity: "base" });
    });
    return arr;
  }, [medecins]);

  const stats = useMemo(() => {
    const total = sorted.length;
    const interieurs = sorted.filter((m) => normalizeName(m.origine) === "interieur").length;
    const exterieurs = total - interieurs;
    return { total, interieurs, exterieurs };
  }, [sorted]);

  return (
    <div style={{ maxWidth: "1050px", margin: "40px auto", padding: "24px", fontFamily: "Inter, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 32, fontWeight: 800, marginBottom: 8 }}>
            👥 Acces medecins planning Port Royal
          </h1>
          <p style={{ color: "#374151", margin: 0 }}>
            Liste PR1 avec origine interne/exterieure. Eligibles desiderata planning PR: internes uniquement.
          </p>
        </div>
        <LogoutButton />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 10, marginBottom: 16 }}>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff", padding: 12 }}>
          <div style={{ color: "#6b7280", fontSize: 13 }}>Total PR1</div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>{stats.total}</div>
        </div>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff", padding: 12 }}>
          <div style={{ color: "#6b7280", fontSize: 13 }}>Interieurs (eligibles)</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#065f46" }}>{stats.interieurs}</div>
        </div>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff", padding: 12 }}>
          <div style={{ color: "#6b7280", fontSize: 13 }}>Exterieurs (non eligibles)</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#1e3a8a" }}>{stats.exterieurs}</div>
        </div>
      </div>

      <div style={{ marginBottom: 12, color: "#374151", fontSize: 14 }}>
        Pour modifier la liste PR1 ou l'origine interne/exterieure, utilisez la gestion des listes medecins PR1.
      </div>

      {loading ? (
        <div style={{ color: "#6b7280" }}>Chargement...</div>
      ) : error ? (
        <div style={{ color: "#b91c1c", fontWeight: 800 }}>❌ {error}</div>
      ) : (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflowX: "auto", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Nom</th>
                <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Prenom</th>
                <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Statut</th>
                <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Origine PR1</th>
                <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Eligibilite desiderata planning PR</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m, idx) => {
                const origine = normalizeName(m.origine) === "exterieur" ? "exterieur" : "interieur";
                const eligible = origine === "interieur";
                return (
                  <tr key={`${m.medecin_id || ""}-${m.nom || ""}-${m.prenom || ""}-${idx}`}>
                    <td style={{ padding: 10, borderBottom: "1px solid #f3f4f6", fontWeight: 700 }}>{m.nom || "—"}</td>
                    <td style={{ padding: 10, borderBottom: "1px solid #f3f4f6" }}>{m.prenom || "—"}</td>
                    <td style={{ padding: 10, borderBottom: "1px solid #f3f4f6" }}>{m.statut || "—"}</td>
                    <td style={{ padding: 10, borderBottom: "1px solid #f3f4f6" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          padding: "4px 10px",
                          borderRadius: 9999,
                          fontWeight: 800,
                          fontSize: 12,
                          background: origine === "interieur" ? "rgba(16,185,129,0.12)" : "rgba(59,130,246,0.12)",
                          color: origine === "interieur" ? "#065f46" : "#1e3a8a",
                        }}
                      >
                        {origine === "interieur" ? "Interieur" : "Exterieur"}
                      </span>
                    </td>
                    <td style={{ padding: 10, borderBottom: "1px solid #f3f4f6" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          padding: "4px 10px",
                          borderRadius: 9999,
                          fontWeight: 800,
                          fontSize: 12,
                          background: eligible ? "rgba(16,185,129,0.12)" : "rgba(220,38,38,0.12)",
                          color: eligible ? "#065f46" : "#991b1b",
                        }}
                      >
                        {eligible ? "Eligible" : "Non eligible"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 16, color: "#6b7280" }}>
                    Aucun medecin trouve dans la liste PR1.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          style={{ padding: "12px 16px", borderRadius: 12, border: "1px solid #111827", background: "#111827", color: "#fff", cursor: "pointer", fontWeight: 800 }}
          onClick={() => navigate("/responsable/planning-pr")}
        >
          ⬅️ Retour planning Port Royal
        </button>
        <button
          style={{ padding: "12px 16px", borderRadius: 12, border: "1px solid #374151", background: "#374151", color: "#fff", cursor: "pointer", fontWeight: 800 }}
          onClick={() => {
            const back = encodeURIComponent(
              `${window.location.pathname}${window.location.search || ""}`
            );
            navigate(`/responsable/gardes-liste-medecins?type=PR1&back=${back}`);
          }}
        >
          ⚙️ Modifier la liste PR1 (admin)
        </button>
      </div>
    </div>
  );
}
